/**
 * Keyboard cursor navigator for canvas-based games.
 *
 * Maintains an integer cursor index over a flat sequence of "slots" and emits
 * a focus-change event whenever the cursor moves. The cursor lives entirely
 * in keyboard semantics — consumers translate the emitted index into pointer
 * coordinates and feed them into the existing move-selector code, so the
 * navigator itself stays render-agnostic.
 *
 * Two layouts:
 *   - `linear`  — 1-D walk, used by backgammon (24 path points + bear-off).
 *                 Arrow Right/Down → +1, Arrow Left/Up → -1.
 *   - `grid`    — 2-D walk on a `cols × rows` board, used by checkers (8×8).
 *                 Arrows move on the rendered axes; the index encoding is
 *                 row-major: `index = row * cols + col`.
 *
 * The cursor never wraps. Wrap-around in a non-contiguous logical space
 * (e.g. between the head and bear-off) confuses both eyes and screen readers,
 * so the rule is "past the ends, nothing happens".
 *
 * Home / End jump to the first / last slot (linear) or the row-start /
 * row-end (grid). PageUp / PageDown jump to the first / last cell of the
 * current column on a grid; on a linear navigator they behave like Home/End.
 *
 * The navigator owns no DOM. Consumers wire `handleKey(event)` into whatever
 * keydown listener already exists; the navigator returns `true` when it
 * consumed the event so consumers can `preventDefault()` correctly.
 */

export type NavigatorMode = "linear" | "grid";

export interface LinearLayout {
  readonly mode: "linear";
  readonly length: number;
}

export interface GridLayout {
  readonly mode: "grid";
  readonly cols: number;
  readonly rows: number;
}

export type NavigatorLayout = LinearLayout | GridLayout;

export interface FocusChangeEvent {
  readonly index: number;
  readonly previous: number;
}

export type FocusChangeListener = (event: FocusChangeEvent) => void;

export interface KeyboardNavigatorOptions {
  readonly layout: NavigatorLayout;
  /** Initial cursor index — defaults to 0. Must be in range. */
  readonly initialIndex?: number;
  /**
   * Swap the grid Y axis. With `false` (default), Arrow Down moves
   * `row + 1`. Some game boards (e.g. checkers viewed from White's side) want
   * "down" to mean "toward the bottom of the board" regardless of row index;
   * setting this flips the sign so consumers don't have to translate.
   */
  readonly invertVertical?: boolean;
}

export class KeyboardNavigator {
  private readonly layout: NavigatorLayout;
  private readonly invertVertical: boolean;
  private cursor: number;
  private listeners: FocusChangeListener[] = [];

  constructor(opts: KeyboardNavigatorOptions) {
    this.layout = opts.layout;
    this.invertVertical = opts.invertVertical === true;
    const initial = opts.initialIndex ?? 0;
    this.cursor = this.clamp(initial);
  }

  /** Current cursor index in row-major form. */
  getIndex(): number {
    return this.cursor;
  }

  /** Linear layouts return null cols/rows. */
  getLayout(): NavigatorLayout {
    return this.layout;
  }

  /** Set the cursor explicitly. Out-of-range values are clamped. Emits if it actually moved. */
  setIndex(next: number): void {
    const clamped = this.clamp(next);
    if (clamped === this.cursor) return;
    const previous = this.cursor;
    this.cursor = clamped;
    this.emit(previous);
  }

  onFocusChange(listener: FocusChangeListener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  /**
   * Handle a keydown. Returns true when the navigator consumed the key (the
   * caller should typically `preventDefault()` in that case).
   */
  handleKey(key: string): boolean {
    const next = this.computeNext(key);
    if (next === null) return false;
    this.setIndex(next);
    return true;
  }

  private computeNext(key: string): number | null {
    if (this.layout.mode === "linear") {
      return this.computeNextLinear(key);
    }
    return this.computeNextGrid(key);
  }

  private computeNextLinear(key: string): number | null {
    const last = this.layout.mode === "linear" ? this.layout.length - 1 : 0;
    switch (key) {
      case "ArrowRight":
      case "ArrowDown":
        return Math.min(last, this.cursor + 1);
      case "ArrowLeft":
      case "ArrowUp":
        return Math.max(0, this.cursor - 1);
      case "Home":
      case "PageUp":
        return 0;
      case "End":
      case "PageDown":
        return last;
      default:
        return null;
    }
  }

  private computeNextGrid(key: string): number | null {
    if (this.layout.mode !== "grid") return null;
    const { cols, rows } = this.layout;
    const col = this.cursor % cols;
    const row = Math.floor(this.cursor / cols);
    const vSign = this.invertVertical ? -1 : 1;
    switch (key) {
      case "ArrowRight":
        return col < cols - 1 ? this.cursor + 1 : this.cursor;
      case "ArrowLeft":
        return col > 0 ? this.cursor - 1 : this.cursor;
      case "ArrowDown": {
        const next = row + vSign;
        return next >= 0 && next < rows ? next * cols + col : this.cursor;
      }
      case "ArrowUp": {
        const next = row - vSign;
        return next >= 0 && next < rows ? next * cols + col : this.cursor;
      }
      case "Home":
        return row * cols;
      case "End":
        return row * cols + (cols - 1);
      case "PageUp":
        return col;
      case "PageDown":
        return (rows - 1) * cols + col;
      default:
        return null;
    }
  }

  private clamp(index: number): number {
    const last = this.layout.mode === "linear"
      ? this.layout.length - 1
      : this.layout.cols * this.layout.rows - 1;
    if (!Number.isFinite(index)) return 0;
    return Math.max(0, Math.min(last, Math.floor(index)));
  }

  private emit(previous: number): void {
    const event: FocusChangeEvent = { index: this.cursor, previous };
    for (const listener of this.listeners) {
      listener(event);
    }
  }
}
